import { Injectable, inject } from '@angular/core';
import { DEFAULT_CLIENT_RUNTIME_LIMITS, ClientRuntimeKind, ClientRuntimeLimits, ClientRuntimeLimitsService } from './client-runtime-limits.service';
import { renderLimitLabel } from '../../constants/runtime.constants';

export type ClientRenderSurface =
  | 'smart-video'
  | 'review-video'
  | 'review-image-sequence'
  | 'processing-video'
  | 'social-template-video'
  | 'social-upload-video'
  | 'audio-analysis';

/**
 * The background Web Worker (`BackgroundRenderClientService`) is the SOLE renderer
 * for every web/WebView flow, so `resolve()` only ever yields `background-worker`
 * (when the worker is supported) or `unavailable`. The remaining literals are kept
 * so legacy callers that still compare against them continue to type-check; they are
 * never produced. Removing them from the union would be a breaking change for those
 * comparisons and is out of scope for this prune.
 */
export type ClientRenderEngine =
  | 'background-worker'
  | 'desktop-review-ffmpeg'
  | 'desktop-ffmpeg'
  | 'mobile-native'
  | 'mobile-client'
  | 'browser-webcodecs'
  | 'browser-media-recorder'
  | 'unavailable';

export type ClientRenderProviderRoute = 'client-byok' | 'server-non-byok';

export interface ClientRenderRuntimeRequest {
  surface: ClientRenderSurface;
  durationSeconds?: number | null;
  isByokUser?: boolean;
  preferDesktopPlugin?: boolean;
  preferWebCodecs?: boolean;
  requiresDesktopFfmpeg?: boolean;
  requiresVideoEncoding?: boolean;
}

export interface ClientRenderRuntimeDecision {
  surface: ClientRenderSurface;
  runtime: ClientRuntimeKind;
  engine: ClientRenderEngine;
  providerRoute: ClientRenderProviderRoute;
  allowed: boolean;
  maxSeconds: number;
  maxLabel: string;
  message?: string;
  reasons: string[];
}

@Injectable({ providedIn: 'root' })
export class ClientRenderRuntimeBridgeService {
  private readonly runtimeLimits = inject(ClientRuntimeLimitsService);

  runtimeKind(): ClientRuntimeKind {
    return this.runtimeLimits.runtimeKind();
  }

  loadRuntimeLimits(): Promise<ClientRuntimeLimits> {
    return this.runtimeLimits.loadOnce();
  }

  maxSeconds(kind: ClientRuntimeKind = this.runtimeKind()): number {
    return this.runtimeLimits.maxSeconds(kind) ?? this.defaultMaxSeconds(kind);
  }

  /**
   * Upfront capability check the UI can call BEFORE a user invests in a render.
   * True when the background worker (the ACTUAL encode path) is supported on this
   * browser. Mirrors `selectEngine`'s `unavailable` condition without changing it.
   */
  isRenderSupported(): boolean {
    return this.supportsBackgroundWorker();
  }

  /**
   * A user-facing reason video rendering can't run on this browser, or null when it
   * can. Lets a flow show "Video rendering needs Chrome or Edge" up front instead of
   * letting the user reach the end of a render and get `unavailable`. Read-only — does
   * NOT affect engine selection.
   */
  renderUnsupportedReason(): string | null {
    if (this.isRenderSupported()) return null;
    return 'Video rendering needs Chrome or Edge. This browser is missing the required '
      + 'video encoding support (WebCodecs / OffscreenCanvas / Web Workers).';
  }

  resolve(request: ClientRenderRuntimeRequest): ClientRenderRuntimeDecision {
    const runtime = this.runtimeKind();
    const supportsBackgroundWorker = this.supportsBackgroundWorker();
    const requiresVideoEncoding = request.requiresVideoEncoding !== false;

    const engine = this.selectEngine(requiresVideoEncoding, supportsBackgroundWorker);
    const maxSeconds = this.maxSeconds(runtime);
    const duration = this.safeDuration(request.durationSeconds);
    const reasons: string[] = [];

    if (duration !== null && duration > maxSeconds) {
      reasons.push(`${this.surfaceLabel(request.surface)} is limited to ${renderLimitLabel(maxSeconds)} on this runtime.`);
    }
    if (engine === 'background-worker' && requiresVideoEncoding) {
      // The worker is the real encode path: gate on ITS requirements
      // (Worker + OffscreenCanvas + WebCodecs), never MediaRecorder/canvas-capture.
      if (typeof Worker === 'undefined') reasons.push('Web Workers are not available.');
      if (typeof OffscreenCanvas === 'undefined') reasons.push('OffscreenCanvas is not available.');
      if (!this.supportsWebCodecs()) reasons.push('WebCodecs is not available.');
    }
    if (engine === 'unavailable') {
      reasons.push(this.unavailableMessage(runtime));
    }

    return {
      surface: request.surface,
      runtime,
      engine,
      providerRoute: request.isByokUser ? 'client-byok' : 'server-non-byok',
      allowed: reasons.length === 0,
      maxSeconds,
      maxLabel: renderLimitLabel(maxSeconds),
      message: reasons.length ? reasons.join(' ') : undefined,
      reasons
    };
  }

  /**
   * The background render worker is the ACTUAL encode path for every web/WebView
   * flow. When an encode is required and the worker is supported, route to it;
   * otherwise the runtime cannot render.
   */
  private selectEngine(requiresVideoEncoding: boolean, supportsBackgroundWorker: boolean): ClientRenderEngine {
    return requiresVideoEncoding && supportsBackgroundWorker ? 'background-worker' : 'unavailable';
  }

  private defaultMaxSeconds(kind: ClientRuntimeKind): number {
    if (kind === 'desktop') return DEFAULT_CLIENT_RUNTIME_LIMITS.desktopMaxSeconds;
    if (kind === 'mobile') return DEFAULT_CLIENT_RUNTIME_LIMITS.mobileMaxSeconds;
    return DEFAULT_CLIENT_RUNTIME_LIMITS.browserMaxSeconds;
  }

  private supportsWebCodecs(): boolean {
    return typeof VideoEncoder !== 'undefined' && typeof VideoDecoder !== 'undefined';
  }

  /**
   * Real capability probe for the ACTUAL encode path: the background render
   * worker (`BackgroundRenderClientService`). It needs a Web Worker, an
   * OffscreenCanvas to composite on, and WebCodecs (VideoEncoder + VideoDecoder).
   * This mirrors `BackgroundRenderClientService.supported()` exactly so the bridge
   * gates on what the worker truly requires.
   */
  private supportsBackgroundWorker(): boolean {
    return typeof Worker !== 'undefined'
      && typeof OffscreenCanvas !== 'undefined'
      && this.supportsWebCodecs();
  }

  private safeDuration(value: number | null | undefined): number | null {
    return typeof value === 'number' && Number.isFinite(value) && value > 0 ? value : null;
  }

  private surfaceLabel(surface: ClientRenderSurface): string {
    switch (surface) {
      case 'smart-video':
        return 'Smart Video rendering';
      case 'review-video':
        return 'Review rendering';
      case 'review-image-sequence':
        return 'Image sequence rendering';
      case 'processing-video':
        return 'Processing render';
      case 'social-template-video':
        return 'Template rendering';
      case 'social-upload-video':
        return 'Post upload rendering';
      case 'audio-analysis':
        return 'Audio analysis';
    }
  }

  private unavailableMessage(runtime: ClientRuntimeKind): string {
    if (runtime === 'desktop') {
      return 'Desktop rendering is unavailable and this browser runtime cannot encode the video.';
    }
    if (runtime === 'mobile') {
      return 'Mobile rendering is unavailable on this device.';
    }
    return 'Browser rendering is unavailable on this device.';
  }
}
